import { makeApi, Zodios, type ZodiosOptions } from '@zodios/core';
import { z } from 'zod';

const YYYY_MM_DD = z.string();
const motivo = z.enum([
  'pedidoDeDemissao',
  'semJustaCausa',
  'comJustaCausa',
  'mutuoAcordo',
  'falecimento',
  'extincaoDeContratoDeExperiencia',
  'rescisaoAntecipadaDoContratoDeExperienciaPeloEmpregador',
  'rescisaoAntecipadaDoContratoDeExperienciaPeloEmpregado',
  'rescisaoJovemAprendizATermo',
]);
const comoLidarComAvisoPrevioNaoCumprido = z.enum([
  'indenizado',
  'dispensado',
  'impedidoDeTrabalhar',
  'empregadoTemNovoEmprego',
]);
const terminationBrProps = z
  .object({
    motivo: motivo,
    comoLidarComAvisoPrevioNaoCumprido:
      comoLidarComAvisoPrevioNaoCumprido.optional(),
  })
  .strict()
  .passthrough();
const terminationProps = z
  .object({
    noticeDate: YYYY_MM_DD.regex(
      /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/
    ),
    terminationDate: YYYY_MM_DD.regex(
      /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/
    ),
    paymentDate: YYYY_MM_DD.regex(
      /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/
    ).optional(),
    br: terminationBrProps,
  })
  .strict()
  .passthrough();
const contractProps = z
  .object({
    companyId: z.string().uuid(),
    contractId: z.string().uuid(),
    br: z
      .object({
        multaFgts: z.string(),
        valorBaseFgts: z.string(),
        diasRestantesNoContratoDeExperiencia: z.number(),
      })
      .partial()
      .strict()
      .passthrough()
      .optional(),
  })
  .strict()
  .passthrough();
const simulationInput = z
  .object({
    name: z.string().min(1).max(50),
    terminationProps: terminationProps,
    contracts: z.array(contractProps).min(1),
  })
  .strict()
  .passthrough();
const simulationStatus = z.enum([
  'submitted',
  'processing',
  'error',
  'completed',
]);
const simulationTotals = z
  .object({
    netPay: z.string(),
    totalCost: z.string(),
    workerEarningsTotal: z.string(),
    workerDeductionsTotal: z.string(),
  })
  .strict()
  .passthrough();
const simulationTotalizers = z
  .object({
    counts: z
      .object({ completed: z.number(), error: z.number() })
      .strict()
      .passthrough(),
    totals: simulationTotals,
  })
  .strict()
  .passthrough();
const simulationEntry = z
  .object({
    organizationId: z.string().uuid(),
    simulationId: z.string().uuid(),
    name: z.string(),
    status: simulationStatus,
    terminationProps: terminationProps,
    contracts: z.array(contractProps),
    error: z
      .object({ code: z.string().optional(), message: z.string() })
      .strict()
      .passthrough()
      .optional(),
    totalizers: simulationTotalizers.optional(),
    createdOn: z.string(),
    createdBy: z.string(),
  })
  .strict()
  .passthrough();
const pagination = z
  .object({
    size: z.number().int().gte(1).lte(100),
    page: z.number().int().gte(0),
  })
  .partial()
  .strict()
  .passthrough();
const sortField = z
  .object({
    field: z.string(),
    order: z.enum(['asc', 'desc']).optional(),
    missing: z.enum(['_first', '_last']).optional(),
  })
  .strict()
  .passthrough();
const sorting = z.array(sortField);
const filteringObject = z.object({ not: z.string() }).strict().passthrough();
const emptyFilter = z.object({ empty: z.boolean() }).strict().passthrough();
const YYYY_MM = z.string();
const DateTime = z.string();
const range = z
  .object({
    max: z.union([z.number(), YYYY_MM, YYYY_MM_DD, DateTime]),
    min: z.union([z.number(), YYYY_MM, YYYY_MM_DD, DateTime]),
  })
  .partial()
  .strict()
  .passthrough();
const filteringInput = z
  .object({
    elements: z.record(
      z.array(z.union([z.string(), filteringObject, emptyFilter]))
    ),
    arrays: z.record(z.array(z.union([z.string(), filteringObject]))),
    ranges: z.record(range),
    toggles: z.array(z.string()),
    nested: z.record(
      z.array(
        z.union([
          z.boolean(),
          z.number(),
          z.string(),
          z
            .object({ not: z.union([z.string(), z.number(), z.boolean()]) })
            .strict()
            .passthrough(),
        ])
      )
    ),
  })
  .partial()
  .strict()
  .passthrough();
const inputCounters = z
  .object({ total: z.boolean(), byProp: z.record(z.array(z.string())) })
  .partial()
  .strict()
  .passthrough();
const countingInput = z
  .object({ filtered: inputCounters, unfiltered: inputCounters })
  .partial()
  .strict()
  .passthrough();
const searchInput = z
  .object({
    query: z.string(),
    pagination: pagination,
    sorting: sorting,
    filtering: filteringInput,
    counting: countingInput,
  })
  .partial()
  .strict()
  .passthrough();
const paginatedResult = z
  .object({
    unfilteredTotal: z.number().optional(),
    total: z.number(),
    size: z.number(),
    page: z.number(),
  })
  .strict()
  .passthrough();
const outputCounters = z
  .object({
    counters: z
      .object({ total: z.number(), byProp: z.record(z.record(z.number())) })
      .partial()
      .strict()
      .passthrough(),
  })
  .strict()
  .passthrough();
const metadataResult = z
  .object({
    metadata: z
      .object({
        filtered: outputCounters,
        unfiltered: outputCounters,
        buckets: outputCounters,
      })
      .partial()
      .strict()
      .passthrough(),
  })
  .partial()
  .strict()
  .passthrough();
const simulationSummary = z
  .object({
    organizationId: z.string().uuid(),
    simulationId: z.string().uuid(),
    name: z.string(),
    status: simulationStatus,
    noticeDate: YYYY_MM_DD.regex(
      /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/
    ),
    terminationDate: YYYY_MM_DD.regex(
      /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/
    ),
    br: terminationBrProps,
    contracts: z.array(z.string()),
    error: z
      .object({ code: z.string().optional(), message: z.string() })
      .strict()
      .passthrough()
      .optional(),
    totalizers: simulationTotalizers.optional(),
    createdOn: z.string(),
    createdBy: z.string(),
  })
  .strict()
  .passthrough();
const simulationList = paginatedResult.and(metadataResult).and(
  z
    .object({ unfilteredTotal: z.number(), data: z.array(simulationSummary) })
    .partial()
    .strict()
    .passthrough()
);
const simulationDataStatus = z.enum(['processing', 'error', 'completed']);
const simulationDataSummary = z
  .object({
    organizationId: z.string().uuid(),
    simulationId: z.string().uuid(),
    companyId: z.string().uuid(),
    contractId: z.string().uuid(),
    status: simulationDataStatus,
    totals: simulationTotals.optional(),
    error: z
      .object({ code: z.string().optional(), message: z.string() })
      .strict()
      .passthrough()
      .optional(),
  })
  .strict()
  .passthrough();
const simulationDataList = paginatedResult.and(metadataResult).and(
  z
    .object({
      unfilteredTotal: z.number(),
      data: z.array(simulationDataSummary),
    })
    .partial()
    .strict()
    .passthrough()
);
const simulationDataPayrollInputs = z
  .object({
    termination: z
      .object({
        noticeDate: YYYY_MM_DD.regex(
          /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/
        ),
        terminationDate: YYYY_MM_DD.regex(
          /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/
        ),
        paymentDate: YYYY_MM_DD.regex(
          /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/
        ).optional(),
        br: z
          .object({
            motivo: motivo,
            comoLidarComAvisoPrevioNaoCumprido:
              comoLidarComAvisoPrevioNaoCumprido.optional(),
            multaFgts: z.string().optional(),
            valorBaseFgts: z.string().optional(),
            diasRestantesNoContratoDeExperiencia: z.number().optional(),
          })
          .strict()
          .passthrough(),
      })
      .strict()
      .passthrough(),
  })
  .strict()
  .passthrough();
const payrollOutputSection = z
  .object({ total: z.string(), elements: z.array(z.string()) })
  .strict()
  .passthrough();
const element = z
  .object({
    type: z.enum(['deductions', 'earnings']),
    id: z.string(),
    name: z.string(),
    amount: z.string(),
  })
  .strict()
  .passthrough();
const basis = z
  .object({ elements: z.array(element), total: z.string() })
  .strict()
  .passthrough();
const description = z
  .object({
    label: z.string(),
    value: z.string(),
    valueType: z.enum(['money', 'percentage', 'days', 'raw', 'hours']),
  })
  .strict()
  .passthrough();
const compensation = z
  .object({
    items: z.array(
      z.object({ label: z.string(), value: z.string() }).strict().passthrough()
    ),
    total: z.string(),
  })
  .strict()
  .passthrough();
const proportionCalculationExplanation = z
  .object({
    type: z.literal('proportion'),
    descriptions: z.array(description),
    expanded: z.string(),
    compensation: compensation.optional(),
  })
  .strict()
  .passthrough();
const flatTableCalculationExplanation = z
  .object({
    type: z.literal('flatTable'),
    brackets: z.array(
      z
        .object({
          lowerBound: z.string(),
          upperBound: z.string(),
          rate: z.string(),
          isRateEffective: z.boolean(),
          totalInBracket: z.string(),
          calculationHint: z.string(),
        })
        .strict()
        .passthrough()
    ),
    total: z.string(),
    compensation: compensation.optional(),
  })
  .strict()
  .passthrough();
const progressiveTableCalculationExplanation = z
  .object({
    type: z.literal('progressive'),
    brackets: z.array(
      z
        .object({
          lowerBound: z.string(),
          upperBound: z.string(),
          rate: z.string(),
          totalInBracket: z.string(),
          calculationHint: z.string(),
        })
        .strict()
        .passthrough()
    ),
    total: z.string(),
    compensation: compensation.optional(),
  })
  .strict()
  .passthrough();
const progressiveTableWithDeductionCalculationExplanation = z
  .object({
    type: z.literal('progressiveTableWithDeduction'),
    brackets: z.array(
      z
        .object({
          lowerBound: z.string(),
          upperBound: z.string(),
          rate: z.string(),
          deduction: z.string(),
          isRateEffective: z.boolean(),
          totalInBracket: z.string(),
          calculationHint: z.string(),
        })
        .strict()
        .passthrough()
    ),
    total: z.string(),
    compensation: compensation.optional(),
  })
  .strict()
  .passthrough();
const body = z.union([
  proportionCalculationExplanation,
  flatTableCalculationExplanation,
  progressiveTableCalculationExplanation,
  progressiveTableWithDeductionCalculationExplanation,
]);
const explanation = z
  .object({
    basis: basis,
    body: body.optional(),
    message: z
      .object({ title: z.string(), description: z.string() })
      .partial()
      .strict()
      .passthrough()
      .optional(),
  })
  .strict()
  .passthrough();
const alert = z
  .object({ type: z.enum(['info', 'warning']), message: z.string().optional() })
  .strict()
  .passthrough();
const payrollMeta = z
  .object({ explanation: explanation, alert: alert })
  .partial()
  .strict()
  .passthrough();
const payrollElement = z
  .object({
    type: z.enum(['deductions', 'earnings']),
    id: z.string(),
    name: z.string(),
    amount: z.string(),
    meta: payrollMeta.optional(),
  })
  .strict()
  .passthrough();
const payrollRecord = z
  .object({
    type: z.string(),
    payload: z.object({}).partial().strict().passthrough(),
  })
  .strict()
  .passthrough();
const payrollOutputTermination = z
  .object({
    paymentDeadline: YYYY_MM_DD.regex(
      /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/
    ),
    paymentDate: YYYY_MM_DD.regex(
      /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/
    ),
    terminationReason: z.string(),
    terminationDate: YYYY_MM_DD.regex(
      /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/
    ),
    noticeDate: YYYY_MM_DD.regex(
      /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/
    ),
    startedBy: z.enum(['employer', 'employee', 'both', 'other']),
    noticeIndemnityPayer: z.enum(['employer', 'employee', 'none']),
    noticeIndemnityType: z
      .enum(['indemnified', 'deducted', 'exempt', 'worked', 'other'])
      .optional(),
    indemnityDaysPaid: z.number().int(),
    noticeProjectedDate: YYYY_MM_DD.regex(
      /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/
    ),
  })
  .strict()
  .passthrough();
const simulationDataPayrollOutputs = z
  .object({
    calculationDate: YYYY_MM_DD.regex(
      /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/
    ).optional(),
    netPay: payrollOutputSection.optional(),
    totalCost: payrollOutputSection.optional(),
    workerEarningsTotal: payrollOutputSection.optional(),
    workerDeductionsTotal: payrollOutputSection.optional(),
    result: z.string().optional(),
    elements: z.record(payrollElement).optional(),
    records: z.record(payrollRecord).optional(),
    termination: payrollOutputTermination,
  })
  .strict()
  .passthrough();
const payrollWorkerData = z
  .object({
    name: z.string(),
    employeeId: z.string(),
    salary: z.string(),
    hours: z.string(),
    admissionDate: YYYY_MM_DD.regex(
      /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/
    ),
    dependentsQuantity: z.string(),
    location: z
      .object({ city: z.string(), state: z.string() })
      .strict()
      .passthrough(),
    jobTitle: z.string(),
    departmentId: z.string().uuid().optional(),
    hiringType: z.string(),
    br: z
      .object({
        mesBaseSindicato: z.number(),
        cnpjSindicato: z.string().min(14).max(14).optional(),
        dependentes: z.array(
          z
            .object({ nmDep: z.string(), tpDep: z.number() })
            .strict()
            .passthrough()
        ),
      })
      .strict()
      .passthrough()
      .optional(),
  })
  .strict()
  .passthrough();
const simulationDataEntry = z
  .object({
    organizationId: z.string().uuid(),
    simulationId: z.string().uuid(),
    companyId: z.string().uuid(),
    contractId: z.string().uuid(),
    status: simulationDataStatus,
    inputs: simulationDataPayrollInputs.optional(),
    outputs: simulationDataPayrollOutputs.optional(),
    workerData: payrollWorkerData.optional(),
    error: z
      .object({ code: z.string().optional(), message: z.string() })
      .strict()
      .passthrough()
      .optional(),
  })
  .strict()
  .passthrough();

export const schemas = {
  YYYY_MM_DD,
  motivo,
  comoLidarComAvisoPrevioNaoCumprido,
  terminationBrProps,
  terminationProps,
  contractProps,
  simulationInput,
  simulationStatus,
  simulationTotals,
  simulationTotalizers,
  simulationEntry,
  pagination,
  sortField,
  sorting,
  filteringObject,
  emptyFilter,
  YYYY_MM,
  DateTime,
  range,
  filteringInput,
  inputCounters,
  countingInput,
  searchInput,
  paginatedResult,
  outputCounters,
  metadataResult,
  simulationSummary,
  simulationList,
  simulationDataStatus,
  simulationDataSummary,
  simulationDataList,
  simulationDataPayrollInputs,
  payrollOutputSection,
  element,
  basis,
  description,
  compensation,
  proportionCalculationExplanation,
  flatTableCalculationExplanation,
  progressiveTableCalculationExplanation,
  progressiveTableWithDeductionCalculationExplanation,
  body,
  explanation,
  alert,
  payrollMeta,
  payrollElement,
  payrollRecord,
  payrollOutputTermination,
  simulationDataPayrollOutputs,
  payrollWorkerData,
  simulationDataEntry,
};

const endpoints = makeApi([
  {
    method: 'post',
    path: '/api/termination/organizations/:organizationId/simulations',
    alias: 'postTerminationSimulation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: simulationInput,
      },
      {
        name: 'organizationId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: simulationEntry,
  },
  {
    method: 'get',
    path: '/api/termination/organizations/:organizationId/simulations/:simulationId',
    alias: 'getTerminationSimulation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'organizationId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'simulationId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: simulationEntry,
  },
  {
    method: 'get',
    path: '/api/termination/organizations/:organizationId/simulations/:simulationId/data/:contractId',
    alias: 'getTerminationSimulationData',
    requestFormat: 'json',
    parameters: [
      {
        name: 'organizationId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'simulationId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'contractId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: simulationDataEntry,
  },
  {
    method: 'post',
    path: '/api/termination/organizations/:organizationId/simulations/:simulationId/data/search',
    alias: 'searchTerminationSimulationData',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `Search parameters`,
        type: 'Body',
        schema: searchInput.optional(),
      },
      {
        name: 'organizationId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'simulationId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: simulationDataList,
  },
  {
    method: 'post',
    path: '/api/termination/organizations/:organizationId/simulations/search',
    alias: 'searchTerminationSimulations',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `Search parameters`,
        type: 'Body',
        schema: searchInput.optional(),
      },
      {
        name: 'organizationId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: simulationList,
  },
]);

export const terminationApiClient = new Zodios(endpoints);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options);
}
